import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Paper, Chip, Button,
  CircularProgress, Alert, Card, CardContent, Breadcrumbs, Link,
} from '@mui/material';
import {
  ArrowBack, CalendarToday, Category, Article, ArrowForward,
  Home, NavigateNext,
} from '@mui/icons-material';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { fetchKeywordDetail, fetchCorpus } from '../utils/api';
import { colors } from '../styles/theme';

const SENTIMENT_COLORS = { positive:'#2D5A4A', negative:'#9B2C2C', neutral:'#6B7280', mixed:'#C9A227' };
const SENTIMENT_LABELS = { positive:'积极', negative:'消极', neutral:'中性', mixed:'混合' };

const getSentimentLabel = (s) => SENTIMENT_LABELS[s?.toLowerCase()] || s || '未知';

function KeywordPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState(null);
  const [corpus, setCorpus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sentimentStats, setSentimentStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [sources, setSources] = useState(null);

  useEffect(() => {
    loadAll();
  }, [id]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [kw, corpusData] = await Promise.all([
        fetchKeywordDetail(id),
        fetchCorpus({ keyword_id: id, page: 1, page_size: 5 }),
      ]);
      setKeyword(kw);
      setCorpus(corpusData.items);

      const [statsRes, trendsRes, sourcesRes] = await Promise.all([
        fetch('/api/corpus/stats?keyword_id=' + id).then(r => r.json()),
        fetch('/api/analytics/trends?keyword_id=' + id).then(r => r.json()),
        fetch('/api/analytics/sources?keyword_id=' + id).then(r => r.json()),
      ]);
      setSentimentStats(statsRes.sentiments);
      setTrends(trendsRes.trends);
      setSources(sourcesRes.sources);
    } catch (e) {
      setError('加载数据失败');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'60vh' }}>
      <CircularProgress size={48} />
    </Box>
  );

  if (error) return (
    <Container maxWidth="lg" sx={{ py:4 }}>
      <Alert severity="error">{error}</Alert>
    </Container>
  );

  if (!keyword) return null;

  const pieData = sentimentStats
    ? Object.entries(sentimentStats)
        .filter(([k, v]) => v > 0)
        .map(([name, value]) => ({ name: SENTIMENT_LABELS[name] || name, value, color: SENTIMENT_COLORS[name] || '#999' }))
    : [];

  const trendData = trends
    ? Object.entries(trends)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([year, s]) => ({ year, ...s }))
    : [];

  const sourceData = (sources || []).slice(0, 8);

  return (
    <Box sx={{ minHeight:'100vh', backgroundColor:colors.background.default, pb:8 }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, ' + colors.primary.main + ' 0%, ' + colors.primary.dark + ' 100%)',
        color: 'white', py: 4,
      }}>
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <Breadcrumbs
            separator={<NavigateNext sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />}
            sx={{ mb: 2 }}
          >
            <Link component={RouterLink} to="/" sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '0.85rem',
              '&:hover': { color: 'white' },
            }}>
              <Home sx={{ fontSize: 16 }} /> 首页
            </Link>
            <Typography sx={{ color: 'white', fontSize: '0.85rem', fontWeight: 600 }}>
              {keyword.chinese_name}
            </Typography>
          </Breadcrumbs>

          <Typography variant="h2" sx={{
            fontFamily:'"Noto Serif SC",serif', fontWeight:700, mb:1,
            fontSize:{xs:'1.8rem',md:'2.5rem'},
          }}>{keyword.chinese_name}</Typography>
          <Typography variant="h5" sx={{
            fontStyle:'italic', opacity:0.9, fontFamily:'"Playfair Display",serif', mb:2,
          }}>{keyword.english_translations || '暂无英文翻译'}</Typography>
          <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
            <Chip icon={<CalendarToday sx={{color:'white !important'}}/>}
              label={keyword.year + '年'}
              sx={{backgroundColor:'rgba(255,255,255,0.2)',color:'white',fontWeight:500}} />
            {keyword.category && (
              <Chip icon={<Category sx={{color:'white !important'}}/>}
                label={keyword.category}
                sx={{backgroundColor:'rgba(255,255,255,0.2)',color:'white',fontWeight:500}} />
            )}
            <Chip icon={<Article sx={{color:'white !important'}}/>}
              label={(keyword.total_count||0) + ' 条语料'}
              sx={{backgroundColor:'rgba(255,255,255,0.2)',color:'white',fontWeight:500}} />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt:3 }}>
        {/* Charts */}
        <Grid container spacing={3} sx={{ mb:4 }}>
          {/* Sentiment Pie */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p:3, borderRadius:3, height:'100%', boxShadow:'0 4px 20px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" sx={{fontWeight:600,mb:2}}>情感分布</Typography>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      paddingAngle={2} dataKey="value">
                      {pieData.map((e, i) => <Cell key={i} fill={e.color}/>)}
                    </Pie>
                    <RechartsTooltip formatter={(v, n) => [v.toLocaleString() + ' 条', n]}/>
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              ) : <Typography color="text.secondary" sx={{py:4,textAlign:'center'}}>暂无数据</Typography>}
            </Paper>
          </Grid>

          {/* Trend Bar */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p:3, borderRadius:3, height:'100%', boxShadow:'0 4px 20px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" sx={{fontWeight:600,mb:2}}>年度趋势</Typography>
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                    <XAxis dataKey="year" fontSize={11} tick={{fill:'#666'}}/>
                    <YAxis fontSize={11} tick={{fill:'#666'}}/>
                    <RechartsTooltip formatter={(v, n) => [v.toLocaleString() + ' 条', SENTIMENT_LABELS[n]||n]}/>
                    <Legend formatter={(v) => SENTIMENT_LABELS[v]||v}/>
                    <Bar dataKey="positive" stackId="a" fill={SENTIMENT_COLORS.positive} name="positive"/>
                    <Bar dataKey="neutral" stackId="a" fill={SENTIMENT_COLORS.neutral} name="neutral"/>
                    <Bar dataKey="negative" stackId="a" fill={SENTIMENT_COLORS.negative} name="negative"/>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Typography color="text.secondary" sx={{py:4,textAlign:'center'}}>暂无趋势数据</Typography>}
            </Paper>
          </Grid>

          {/* Sources Bar */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p:3, borderRadius:3, height:'100%', boxShadow:'0 4px 20px rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" sx={{fontWeight:600,mb:2}}>媒体来源 Top {sourceData.length}</Typography>
              {sourceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={sourceData} layout="vertical" margin={{left:10,right:20}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                    <XAxis type="number" fontSize={11} tick={{fill:'#666'}}/>
                    <YAxis type="category" dataKey="name" width={120} fontSize={10} tick={{fill:'#666'}}
                      tickFormatter={(v) => v.length > 18 ? v.substring(0,18)+'...' : v}/>
                    <RechartsTooltip formatter={(v) => [v.toLocaleString() + ' 条', '数量']}/>
                    <Bar dataKey="value" fill={colors.primary.main} radius={[0,4,4,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Typography color="text.secondary" sx={{py:4,textAlign:'center'}}>暂无来源数据</Typography>}
            </Paper>
          </Grid>
        </Grid>

        {/* Corpus Preview */}
        <Paper sx={{ p:3, borderRadius:3, boxShadow:'0 4px 20px rgba(0,0,0,0.06)' }}>
          <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',mb:2}}>
            <Typography variant="h6" sx={{fontWeight:600}}>语料预览</Typography>
            <Button variant="outlined" size="small" endIcon={<ArrowForward/>}
              onClick={() => navigate('/corpus/' + id)}>
              查看全部（{keyword.total_count||0} 条）
            </Button>
          </Box>

          {corpus.length === 0 ? (
            <Typography color="text.secondary" sx={{py:3,textAlign:'center'}}>暂无语料数据</Typography>
          ) : (
            <Box sx={{display:'flex',flexDirection:'column',gap:2}}>
              {corpus.map((item) => (
                <Card key={item.id} variant="outlined" sx={{
                  borderLeft:'4px solid ' + (SENTIMENT_COLORS[item.sentence_sentiment_1?.toLowerCase()]||'#ccc'),
                  '&:hover':{backgroundColor:colors.accent.highlight}, transition:'background 0.2s',
                }}>
                  <CardContent sx={{py:2,'&:last-child':{pb:2}}}>
                    <Typography variant="body1" sx={{
                      fontStyle:'italic', lineHeight:1.8, mb:1.5, color:'text.primary',
                    }}>
                      &ldquo;{item.original_sentence?.length > 300
                        ? item.original_sentence.substring(0,300) + '...'
                        : item.original_sentence}&rdquo;
                    </Typography>
                    <Box sx={{display:'flex',gap:1,flexWrap:'wrap',alignItems:'center'}}>
                      {item.sentence_sentiment_1 && (
                        <Chip size="small" label={getSentimentLabel(item.sentence_sentiment_1)}
                          sx={{
                            backgroundColor:(SENTIMENT_COLORS[item.sentence_sentiment_1?.toLowerCase()]||'#999') + '18',
                            color:SENTIMENT_COLORS[item.sentence_sentiment_1?.toLowerCase()]||'#999',
                            fontWeight:500, fontSize:'0.75rem',
                          }}/>
                      )}
                      {item.sentence_topic_1 && (
                        <Chip size="small" label={item.sentence_topic_1}
                          sx={{backgroundColor:colors.accent.cool,fontSize:'0.75rem'}}/>
                      )}
                      <Box sx={{ml:'auto',display:'flex',gap:1.5,alignItems:'center',color:'text.secondary'}}>
                        {item.news_source && <Typography variant="caption">{item.news_source}</Typography>}
                        {item.news_date && <Typography variant="caption">{item.news_date}</Typography>}
                      </Box>
                    </Box>
                    {item.news_title && (
                      <Typography variant="caption" sx={{display:'block',mt:1,color:'text.secondary'}}>
                        {item.news_title}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default KeywordPage;
